import * as Rx from 'rxjs';
import { tap, debounceTime, switchMap, catchError, take, map } from 'rxjs/operators';
import { ERefundMotive } from 'v2/interfaces/requestManagement/refund';

import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import type { IRefund, IRefundMotive } from 'v2/interfaces/requestManagement/refund';

import apiService from '../api';
import tableService from '../table';

import type { ApiService } from '../api';
import type { TableService } from '../table';

export type IOrderBy = { sorty_by: string; order_by: 'desc' | 'asc' };

type IFilter = { order?: IOrderBy };

export class RefundService {
  private list$ = new Rx.BehaviorSubject<IRefund[]>([]);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>({ current_page: 1, per_page: 20 });
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);
  private ordering$ = new Rx.BehaviorSubject<boolean | 'asc' | 'desc'>(false);
  private ordered$ = new Rx.BehaviorSubject<number>(-1);
  private filter$ = new Rx.BehaviorSubject<IFilter>(null);
  private currentRefund$ = new Rx.BehaviorSubject<IRefund>(null);
  private details$ = new Rx.BehaviorSubject<boolean>(false);
  private reproveMotive$ = new Rx.BehaviorSubject<boolean>(false);
  private approve$ = new Rx.BehaviorSubject<boolean>(false);
  private massApprove$ = new Rx.BehaviorSubject<boolean>(false);

  constructor(private apiService: ApiService, private tableService: TableService) {}

  public getDetails = (): Rx.Observable<boolean> => this.details$;

  public getReproveMotive = (): Rx.Observable<boolean> => this.reproveMotive$;

  public getApprove = (): Rx.Observable<boolean> => this.approve$;

  public getMassApprove = (): Rx.Observable<boolean> => this.massApprove$;

  public getCurrentRefund = (): Rx.Observable<IRefund> => this.currentRefund$;

  public openDetails(selectRefund: IRefund) {
    this.currentRefund$.next(selectRefund);
    this.details$.next(true);
  }

  public closeDetails() {
    this.details$.next(false);
    this.currentRefund$.next(null);
  }

  public openReproveMotive(selectRefund: IRefund) {
    this.currentRefund$.next(selectRefund);
    this.reproveMotive$.next(true);
  }

  public closeReproveMotive() {
    this.reproveMotive$.next(false);
    this.currentRefund$.next(null);
  }

  public openApprove(selectRefund: IRefund) {
    this.currentRefund$.next(selectRefund);
    this.approve$.next(true);
  }

  public closeApprove() {
    this.approve$.next(false);
    this.currentRefund$.next(null);
  }

  public openMassApprove = () => this.massApprove$.next(true);

  public closeMassApprove = () => this.massApprove$.next(false);

  public getFilter = (): Rx.Observable<IFilter> => this.filter$;

  public setFilter(filter: IFilter): void {
    this.filter$.pipe(take(1)).subscribe((currentFilter: IFilter) => {
      this.filter$.next({ ...currentFilter, ...filter });
    });
  }

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: IPaginationParams) {
    this.paginationParams$.next(pagination);
  }

  public getPagination = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getOrdering = (): Rx.Observable<boolean | 'asc' | 'desc'> => this.ordering$;

  public setOrdering(ordering: boolean | 'asc' | 'desc'): void {
    this.ordering$.next(ordering);
  }

  public getOrdered = (): Rx.Observable<number> => this.ordered$;

  public setOrdered(ordered: number): void {
    this.ordered$.next(ordered);
  }

  public list(): Rx.Observable<IRefund[]> {
    return this.list$;
  }

  public loadRefunds() {
    return this.getFilter().pipe(
      switchMap((filter) =>
        this.getPaginationParams().pipe(
          map((pagination) => ({
            request_status_id: 'pending',
            sort_by: filter?.order.sorty_by || 'created_at',
            order_by: filter?.order?.order_by || 'asc',
            ...pagination,
          }))
        )
      ),
      tap(() => this.tableService.changeLoading(true)),
      debounceTime(10),
      switchMap((filters) => this.apiService.get('request-management/refund', filters)),
      tap((response) => this.list$.next(response.data)),
      tap((response) => this.paginationResponse$.next({ per_page: response.per_page, current_page: response.page, total_items: response.total })),
      tap(() => this.tableService.changeLoading(false)),
      catchError(() => {
        this.tableService.changeLoading(false);
        this.list$.next([]);
        this.paginationResponse$.next(null);
        return Rx.of([]);
      })
    );
  }

  public checkRefund(id: string) {
    let list = this.list$.value;
    let newList = list.map((o) => {
      if (o.id === id) o.checked = !o.checked;
      return o;
    });
    this.list$.next(newList);
  }

  public checkAllRefunds() {
    let list = this.list$.value;
    let newList = list.map((o) => {
      o.checked = true;
      return o;
    });
    this.list$.next(newList);
  }

  public unCheckAllRefunds() {
    let list = this.list$.value;
    let newList = list.map((o) => {
      o.checked = false;
      return o;
    });
    this.list$.next(newList);
  }

  public listReproveMotive(): Rx.Observable<IRefundMotive[]> {
    return this.apiService.get('request-management/motive', { type: ERefundMotive.REPROVE, visible: true }).pipe(tap((response) => response.data));
  }

  public approve(refund: IRefund) {
    return this.apiService
      .post('request-management/refund/approve', {
        refund_id: refund.id,
      })
      .pipe(
        tap((response) => response.data),
        tap(() => refundService.loadRefunds().subscribe())
      );
  }

  public massApprove(refunds: string[]) {
    return this.apiService.post('request-management/refund/mass-approve', { refund_ids: refunds }).pipe(
      tap((response) => response.data),
      tap(() => refundService.loadRefunds().subscribe())
    );
  }

  public reprove(refund: IRefund, reasonId: string, justification: string) {
    return this.apiService
      .post('request-management/refund/reprove', {
        reprove_motive_id: reasonId,
        reprove_justification: justification,
        refund_id: refund.id,
      })
      .pipe(
        tap((response) => response.data),
        tap(() => refundService.loadRefunds().subscribe())
      );
  }
}

const refundService = new RefundService(apiService, tableService);
export default refundService;
